import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, Maximize2, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { whenText } from "@/components/Paywall";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const KEY = "brynai.homeDraft";
const closedUntil = (ent) => (ent && ent.chat_open === false && ent.resets_at && Date.now() < new Date(ent.resets_at).getTime() ? ent.resets_at : null);

function Box({ value, onChange, onSend, big, taRef }) {
  return (
    <textarea ref={taRef} rows={big ? 12 : 1} value={value} data-testid={big ? "home-composer-expanded-input" : "home-composer-input"}
      aria-label="Message Bryn" placeholder="Ask anything"
      onChange={(e) => { onChange(e.target.value); if (!big) { e.target.style.height = "auto"; e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`; } }}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
      className={`flex-1 min-w-0 resize-none bg-transparent outline-none text-base placeholder:text-neutral-500 ${big ? "h-[50vh] p-1" : "py-2 px-1 max-h-[160px]"}`} />
  );
}

export function HomeComposer() {
  const [text, setTextState] = useState(() => sessionStorage.getItem(KEY) || "");
  const [big, setBig] = useState(false);
  const taRef = useRef(null);
  const navigate = useNavigate();
  const { ent } = useAuth();
  const until = closedUntil(ent);
  const setText = (v) => { setTextState(v); sessionStorage.setItem(KEY, v); };
  const send = () => {
    const t = text.trim();
    if (!t) return;
    if (until) { window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "limit", resets_at: until } })); return; }
    setText("");
    setBig(false);
    navigate("/app/new", { state: { prefill: { text: t } } });
  };
  const sendBtn = (testid) => (
    <button onClick={send} disabled={!text.trim()} data-testid={testid} aria-label="Send"
      className="h-9 w-9 rounded-xl bg-sky-400 text-white flex items-center justify-center hover:bg-sky-500 disabled:opacity-40 transition-colors shrink-0"><ArrowUp className="h-4 w-4" /></button>
  );
  return (
    <div className="sticky bottom-0 z-10 px-3 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-[#0A0D15] via-[#0A0D15]/95 to-transparent" data-testid="home-composer">
      <div className="max-w-3xl mx-auto">
        {until && <p className="mb-2 text-xs text-neutral-400 flex items-center gap-1.5 px-1" data-testid="home-composer-closed"><Clock className="h-3.5 w-3.5 text-sky-300" />Chatting opens again {whenText(until)}.</p>}
        <div className="flex items-end gap-1.5 rounded-2xl border border-white/10 bg-[#121725] p-2 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.9)] focus-within:border-sky-400/50 transition-colors">
          <Box value={text} onChange={setText} onSend={send} taRef={taRef} />
          <button onClick={() => setBig(true)} data-testid="home-composer-expand" aria-label="Expand writing box" title="Expand"
            className="h-9 w-9 rounded-xl flex items-center justify-center text-neutral-400 hover:text-white hover:bg-white/10 shrink-0"><Maximize2 className="h-4 w-4" /></button>
          {sendBtn("home-composer-send")}
        </div>
        <p className="hidden sm:block text-[11px] text-neutral-600 mt-2 px-1 text-center">Enter to send · Shift + Enter for a new line</p>
      </div>
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="bg-[#121725] border-white/10 max-w-3xl sm:rounded-2xl" data-testid="home-composer-dialog">
          <DialogTitle className="font-display">Write your question</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Your text is kept if you close this window.</DialogDescription>
          <div className="flex flex-col gap-3">
            <Box value={text} onChange={setText} onSend={send} big />
            <div className="flex justify-end">{sendBtn("home-composer-expanded-send")}</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
